import React, { useState, useEffect } from "react";
import { shallow } from "zustand/shallow";
import {
  startTask,
  getTaskStatus,
  downloadTaskResult,
} from "../../../../api/TasksApi";
import { useAppStore } from "../../../../store/appStore";
import { useUserState } from "../../../../context/user";
import { copyDel } from "../../../../api/DelApi";
import { getLocalDateString } from "../../../../utils/dateUtils";
import { useFilters } from "../../../../hooks/useFilters";
import { useFilterMenu } from "../../../../hooks/useFilterMenu";
import { filtersInitialState } from "../../../../store/shared/filters/filtersState";
// Components
import DelForm from "../../../delForm/DelForm";
import {
  RefreshDelListsButton,
  FilterButton,
  DownloadPdfButton,
  ViewDelButton,
  CopyDelButton,
  EditDraftButton,
  ApproveButton,
} from "../buttons/DelActions";
import { DelReportingTable } from "../del_table/DelReportingTable";
import { ReportingFilterMenu } from "../../../common/FilterMenu";
import styles from "./DelReportingTab.module.css";

export const DelReportingTab = () => {
  // User Info
  const user = useUserState().currentUser;
  // Employees
  const employees = useAppStore((s) => s.employees.list, shallow);
  // Terminated employees
  const allTerminatedEmployees = useAppStore(
    (s) => s.employees.terminatedList,
    shallow,
  );
  const allSearchEmployees = [...employees, ...allTerminatedEmployees];
  // Projects
  const allProjectInfo = useAppStore((s) => s.projects.list, shallow);
  // Inactive projects
  const allInactiveProjects = useAppStore(
    (s) => s.projects.inactiveList,
    shallow,
  );

  // Loading UI state
  const { isActive } = useAppStore((s) => s.loading, shallow);
  // Loading UI actions
  const showLoading = useAppStore((s) => s.loadingActions.showLoading);
  const hideLoading = useAppStore((s) => s.loadingActions.hideLoading);
  // Notification UI actions
  const showNotification = useAppStore(
    (s) => s.notificationActions.showNotification,
  );

  // Reports List State
  const reports = useAppStore((s) => s.delLists.delReportsLists.items, shallow);
  const totalCount = useAppStore(
    (s) => s.delLists.delReportsLists.totalCount,
    shallow,
  );
  const currentPage = useAppStore(
    (s) => s.delLists.delReportsLists.currentPage,
    shallow,
  );
  const selectedReport = useAppStore(
    (s) => s.delLists.delReportsLists.selectedReport,
    shallow,
  );
  const pageSize = useAppStore(
    (s) => s.delLists.delReportsLists.pageSize,
    shallow,
  );

  // DEL UI States
  const { showDelForm } = useAppStore((s) => s.delReportingTabUI, shallow);
  const activeDel = useAppStore((s) => s.activeDelReporting, shallow);
  const reportDelActions = useAppStore(
    (s) => s.activeDelReportingActions,
    shallow,
  );

  // App Store Actions
  // Workflow Actions
  const fetchDel = useAppStore((s) => s.activeDelReportingActions.fetchDel);

  // Specific DEL UI actions
  const setShowDelForm = useAppStore(
    (s) => s.delReportingTabUIActions.setShowDelForm,
  );

  // Handling Page UI actions
  const pageSizeChange = useAppStore(
    (s) => s.delListsActions.setReportsPageSize,
  );
  const setReportsPage = useAppStore((s) => s.delListsActions.setReportsPage);
  const selectReportRow = useAppStore(
    (s) => s.delListsActions.setSelectedReport,
  );
  // Refreshing DEL Lists, both Drafts and Submits.
  const refreshReportsDelLists = useAppStore(
    (s) => s.delListsActions.fetchDelReportsPage,
  );

  // Local filter state (resets on unmount)
  const { filters: reportsFilters, updateFilter: updateReportingFilter, clearFilters: clearReportingFilters } = useFilters();
  // Filter menu UI state
  const { showFilterMenu, toggleFilterMenu: handleToggleFilterMenu, filterPhaseList, setFilterPhaseList: filterPhaseActionFunction, filterMenuRef } = useFilterMenu();

  const fetchDelReports = useAppStore(
    (s) => s.delListsActions.fetchDelReportsPage,
  );
  // Applying Filters
  const handleApplyFilters = async () => {
    showLoading("Loading DELs...");
    await fetchDelReports(reportsFilters);
    hideLoading();
  };
  // Clearing Filters
  const handleClearFilters = async () => {
    clearReportingFilters();
    showLoading("Loading DELs...");
    await fetchDelReports(filtersInitialState());
    hideLoading();
  };

  // Helper Function to allow the user to edit or view a DEL
  // -- If user.employee_id = created_by in DEL and submit=false, then the user can edit the DEL
  // -- If user.employee_id = invoice_manager in DEL and DEL submit=true and not approved=false, OR user.access_level = 1, then user can approve the DEL
  // -- Else user can open and view DEL.
  const checkDelEditability = (del) => {
    if (user.employee_id === del.user_id && del.submit === "False") {
      return "edit";
    } else if (
      (user.employee_id === del.invoice_manager_id || user.access_level > 0) &&
      del.submit === "True" &&
      del.approved === "False"
    ) {
      return "approve";
    } else {
      return "view";
    }
  };

  const handleDoubleClick = async (del) => {
    showLoading("Loading DEL...");
    const editability = checkDelEditability(del);
    try {
      const result = await fetchDel(editability, del.id);
      if (!result || result.ok !== true) {
        showNotification({
          type: "error",
          title: "Error Loading DEL",
          message: "DEL not found or could not be loaded.",
        });
        return;
      }
      setShowDelForm(true);
      selectReportRow(del);
    } catch (error) {
      window.alert("Error loading DEL, please try again later.");
      console.error("Error loading DEL for edit:", error);
    } finally {
      hideLoading();
    }
  };

  const handleApproveDel = async () => {
    if (!selectedReport) {
      window.alert("Please select a DEL to approve.");
      return;
    }
    try {
      showLoading("Loading DEL for Approval...");
      const result = await fetchDel("approve", selectedReport.id);
      if (!result || result.ok !== true) {
        showNotification({
          type: "error",
          title: "Error Loading DEL for Approval",
          message: "DEL not found or could not be loaded.",
        });
        return;
      }
      setShowDelForm(true);
      selectReportRow(selectedReport);
    } catch (error) {
      window.alert("Error loading DEL for approval, please try again later.");
      console.error("Error loading DEL for approval:", error);
    } finally {
      hideLoading();
    }
  };

  const handleEditDel = async () => {
    if (!selectedReport) {
      window.alert("Please select a DEL to edit.");
      return;
    }
    try {
      showLoading("Loading DEL for Edit...");
      const result = await fetchDel("edit", selectedReport.id);
      if (!result || result.ok !== true) {
        showNotification({
          type: "error",
          title: "Error Loading DEL for Edit",
          message: "DEL not found or could not be loaded.",
        });
        return;
      }
      setShowDelForm(true);
      selectReportRow(selectedReport);
    } catch (error) {
      window.alert("Error loading DEL for edit, please try again later.");
      console.error("Error loading DEL for edit:", error);
    } finally {
      hideLoading();
    }
  };

  const handleViewDel = async () => {
    if (!selectedReport) {
      window.alert("Please select a DEL to view.");
      return;
    }
    try {
      showLoading("Loading DEL for View...");
      const result = await fetchDel("view", selectedReport.id);
      if (!result || result.ok !== true) {
        showNotification({
          type: "error",
          title: "Error Loading DEL for View",
          message: "DEL not found or could not be loaded.",
        });
        return;
      }
      setShowDelForm(true);
      selectReportRow(selectedReport);
    } catch (error) {
      window.alert("Error loading DEL for view, please try again later.");
      console.error("Error loading DEL for view:", error);
    } finally {
      hideLoading();
    }
  };

  const handleCopyDel = async () => {
    if (!selectedReport) {
      window.alert("Please select a DEL to copy.");
      return;
    }
    try {
      showLoading("Copying DEL...");
      const resp = await copyDel(selectedReport.id, user, "reports");
      const { del_id, del_creation_date, summary } = resp || {};
      if (!del_id) {
        throw new Error("Copy failed");
      }
      // Upsert into Drafts list (not this tab's list)
      if (summary) {
        useAppStore.getState().delListsActions.upsertDraftLocal(summary);
      } else {
        useAppStore.getState().delListsActions.upsertDraftLocal({
          id: del_id,
          del_creation_date: del_creation_date || getLocalDateString(),
          updated: getLocalDateString(),
          project_number: "-",
        });
      }
      showNotification({
        type: "success",
        title: "DEL Copied",
        message: "Your DEL has been copied. You can edit it in the Drafts tab.",
      });
      hideLoading();
    } catch (error) {
      hideLoading();
      showNotification({
        type: "error",
        title: "Error Copying DEL",
        message: "Please try again later.",
      });
      console.error("Error copying DEL:", error);
    }
  };

  // Handlers for pagination
  const handlePageSizeChange = async (e) => {
    showLoading("Loading DELs...");
    const newPageSize = parseInt(e.target.value, 10);
    pageSizeChange(newPageSize);
    await fetchDelReports(reportsFilters);
    hideLoading();
  };

  const handlePageChange = async (direction) => {
    showLoading("Loading DELs...");
    let newPage;
    if (direction === "next") {
      newPage = currentPage + 1;
    } else {
      newPage = currentPage - 1;
    }
    setReportsPage({ currentPage: newPage, pageSize });
    await fetchDelReports(reportsFilters);
    hideLoading();
  };

  const handleRefreshDelLists = async () => {
    showLoading("Refreshing DELs...");
    setReportsPage({ currentPage: 1, pageSize });
    await refreshReportsDelLists(reportsFilters);
    hideLoading();
  };

  const handleSelectReportRow = (del) => {
    selectReportRow(del);
  };

  const handleRefreshReportsDelLists = async () => {
    showLoading("Refreshing DELs...");
    setReportsPage({ currentPage: 1, pageSize: pageSize });
    await refreshReportsDelLists(reportsFilters);
    hideLoading();
  };

  // PDF setup
  const toggleSelectedReport = useAppStore(
    (s) => s.delListsActions.toggleSelectedReport,
  );

  const selectedReportIds = useAppStore(
    (s) => s.delLists.delReportsLists.selectedReportIds,
  );
  const handleDownloadSingleReport = async (reportId) => {
    if (!reportId) {
      showNotification({
        type: "error",
        title: "No DEL selected",
        message: "Please select a DEL to download.",
      });
      return;
    }
    showLoading("Downloading DEL...");
    try {
      const { data: start } = await startTask("del_ops.create_single_pdf", {
        del_id: reportId,
      });
      const taskId = start?.task_id;
      if (!taskId) throw new Error("Failed to start PDF task");

      let attempts = 0;
      let ready = false;
      let failed = false;
      let errorMessage = null;
      let lastStatus = null;
      while (!ready && attempts < 60) {
        await new Promise((r) => setTimeout(r, 2000));
        const { data: status } = await getTaskStatus(taskId);
        lastStatus = status;
        ready = !!status?.ready;
        if (ready && !status?.ok) {
          failed = true;
          errorMessage = status?.error || "Task failed";
          break;
        }
        attempts += 1;
      }
      if (!ready) throw new Error("Timed out waiting for PDF");
      if (failed) throw new Error(errorMessage || "Task failed");
      const res = await downloadTaskResult(taskId, {
        s3DownloadUrl: lastStatus?.s3_download_url,
        filename: lastStatus?.filename,
      });
      if (res) {
        const blob = new Blob([res.data], {
          type: res.headers["content-type"] || "application/pdf",
        });
        const cd = res.headers["content-disposition"] || "";
        const match = /filename\*?=(?:UTF-8''|")?([^";]+)/i.exec(cd);
        const filename = match ? decodeURIComponent(match[1]) : "DEL.pdf";
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        a.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (e) {
      showNotification({
        type: "error",
        title: "PDF Download Failed",
        message: e.message || "Unknown error",
      });
    } finally {
      hideLoading();
    }
  };

  const handleClearSelectedReportIds = useAppStore(
    (s) => s.delListsActions.clearSelectedReportIds,
  );
  const handleDownloadPdfs = async () => {
    if (selectedReportIds.length === 0) {
      showNotification({
        type: "error",
        title: "No DELs selected",
        message: "Please select at least one DEL to download.",
      });
      return;
    }
    if (selectedReportIds.length > 50) {
      showNotification({
        type: "error",
        title: "Too many DELs selected",
        message:
          "You can only download up to 50 DELs at a time. Please reduce your selection.",
      });
      return;
    }
    showLoading("Downloading DELs...");
    try {
      let taskName = "";
      let taskData = {};
      if (selectedReportIds.length === 1) {
        taskName = "del_ops.create_single_pdf";
        taskData = {
          del_id: selectedReportIds[0],
        };
      } else {
        taskName = "del_ops.create_pdfs";
        taskData = {
          del_ids: selectedReportIds,
        };
      }
      const { data: start } = await startTask(taskName, taskData);
      const taskId = start?.task_id;
      if (!taskId) throw new Error("Failed to start PDF task");
      let attempts = 0;
      let ready = false;
      let failed = false;
      let errorMessage = null;
      let lastStatus = null;
      // 150 attempts x 2s = 5 minutes for bulk PDFs (up to 50 DELs)
      const maxAttempts = 150;
      while (!ready && attempts < maxAttempts) {
        await new Promise((r) => setTimeout(r, 2000));
        const { data: status } = await getTaskStatus(taskId);
        lastStatus = status;
        ready = !!status?.ready;
        if (ready && !status?.ok) {
          failed = true;
          errorMessage = status?.error || "Task failed";
          break;
        }
        attempts += 1;
      }
      if (!ready)
        throw new Error(
          "Timed out waiting for PDFs. The task may still be running.",
        );
      if (failed) throw new Error(errorMessage || "Task failed");
      const res = await downloadTaskResult(taskId, {
        s3DownloadUrl: lastStatus?.s3_download_url,
        filename: lastStatus?.filename,
      });
      if (res) {
        const blob = new Blob([res.data], {
          type: res.headers["content-type"] || "application/zip",
        });
        const cd = res.headers["content-disposition"] || "";
        const match = /filename\*?=(?:UTF-8''|")?([^";]+)/i.exec(cd);
        const filename = match ? decodeURIComponent(match[1]) : "DELs.zip";
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        a.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (e) {
      showNotification({
        type: "error",
        title: "PDF Download Failed",
        message: e.message || "Unknown error",
      });
    } finally {
      hideLoading();
    }
  };

  const handleToggleSelectReportRow = (reportId) => {
    toggleSelectedReport(reportId);
  };

  // Monitor selection count and warn if > 50
  useEffect(() => {
    if (selectedReportIds.length > 50) {
      showNotification({
        type: "warning",
        title: "Selection limit exceeded",
        message: `You have selected ${selectedReportIds.length} DELs. You can only download 50 at a time. Please deselect some DELs before downloading.`,
      });
    }
  }, [selectedReportIds, showNotification]);

  return (
    <>
      {showDelForm ? (
        <>
          <DelForm
            activeDelTabInfo={activeDel}
            actionFunctions={reportDelActions}
            setShowDelForm={() => setShowDelForm(false)}
            tableName="del_reporting"
            copyDelFunction={handleCopyDel}
            refreshDelListFunction={handleRefreshDelLists}
            unapproveDelOption={true}
          />
        </>
      ) : (
        <>
          <div className={styles.reportingTabContainer}>
            {/* Action buttons */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                flexWrap: "wrap",
              }}
            >
              <ViewDelButton viewFunction={handleViewDel} />
              <EditDraftButton
                editFunction={handleEditDel}
                disabled={
                  selectedReport &&
                  checkDelEditability(selectedReport) !== "edit"
                }
              />
              <CopyDelButton copyFunction={handleCopyDel} />
              <ApproveButton
                approveFunction={handleApproveDel}
                disabled={
                  selectedReport &&
                  checkDelEditability(selectedReport) !== "approve"
                }
              />
            </div>
            <div className={styles.headerWithRefresh}>
              <h2>DEL Reports</h2>

              <div
                style={{
                  position: "relative",
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
                ref={filterMenuRef}
              >
                <DownloadPdfButton
                  onClick={handleDownloadPdfs}
                  disabled={isActive}
                />
                <FilterButton filterFunction={handleToggleFilterMenu} />
                {showFilterMenu && (
                  <ReportingFilterMenu
                    filters={reportsFilters}
                    filterActions={{
                      updateFilter: updateReportingFilter,
                    }}
                    onApplyFilters={handleApplyFilters}
                    onClearFilters={handleClearFilters}
                    allProjectInfo={allProjectInfo}
                    allInactiveProjects={allInactiveProjects}
                    allEmployees={allSearchEmployees}
                    filterPhaseList={filterPhaseList}
                    filterPhaseActionFunction={filterPhaseActionFunction}
                  />
                )}
              </div>
              <RefreshDelListsButton
                refreshFunction={handleRefreshReportsDelLists}
                isLoading={isActive}
              />
            </div>

            <div className={styles.tableWrapper}>
              <div className={styles.reportData}>
                {useAppStore.getState().delLists.delReportsLists.loading &&
                reports.length === 0 ? (
                  <div className={styles.loading}>Loading report data...</div>
                ) : reports.length > 0 ? (
                  <div className={styles.tableContainer}>
                    <DelReportingTable
                      dels={reports}
                      onRowSelect={handleSelectReportRow}
                      onDoubleClick={handleDoubleClick}
                      selectedDelId={selectedReport?.id}
                      selectedIds={selectedReportIds}
                      onToggleSelect={handleToggleSelectReportRow}
                      onDownloadSingle={handleDownloadSingleReport}
                      clearSelectedApprovedIds={handleClearSelectedReportIds}
                    />
                  </div>
                ) : (
                  <div className={styles.noData}>
                    <p>
                      No report data available. Please use the filter button to
                      configure and generate a report.
                    </p>
                  </div>
                )}
              </div>
            </div>
            <div className={styles.paginationControls}>
              <div className={styles.paginationLeftControls}>
                <button
                  onClick={() => handlePageChange("prev")}
                  disabled={currentPage === 1 || isActive}
                >
                  Previous
                </button>
                <button
                  onClick={() => handlePageChange("next")}
                  disabled={currentPage * pageSize >= totalCount || isActive}
                >
                  Next
                </button>
                <div className={styles.pageSizeSelector}>
                  <label htmlFor="pageSize">Rows:</label>
                  <select
                    id="pageSize"
                    className={styles.pageSizeSelect}
                    value={pageSize}
                    onChange={handlePageSizeChange}
                  >
                    <option value="10">10</option>
                    <option value="25">25</option>
                    <option value="50">50</option>
                    <option value="100">100</option>
                    <option value="500">500</option>
                  </select>
                </div>
              </div>
              <span>
                Showing {Math.min((currentPage - 1) * pageSize + 1, totalCount)}{" "}
                - {Math.min(currentPage * pageSize, totalCount)} of {totalCount}{" "}
                reports
              </span>
            </div>
          </div>
        </>
      )}
    </>
  );
};
